import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Dropdown, Avatar, Space } from 'antd';
import {
  HomeOutlined,
  FolderOpenOutlined,
  TeamOutlined,
  WarningOutlined,
  CalendarOutlined,
  UserOutlined,
  LogoutOutlined,
  GlobalOutlined,
  DeleteOutlined,
  SettingOutlined,
  BgColorsOutlined,
  HistoryOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import { palettes } from '@/config/theme';
import { authApi } from '@/api/auth';
import NotificationBell from '@/components/NotificationBell';
import type { MenuProps } from 'antd';

const { Header, Content } = Layout;

export default function MainLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshToken } = useAuthStore();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isImpersonating = useAuthStore((s) => s.isImpersonating)();
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation);
  const { paletteKey, setPalette } = useThemeStore();
  const menuCollapsed = true;

  const currentTab = '/' + (location.pathname.split('/')[1] || 'explorer');

  const menuItems: MenuProps['items'] = [
    { key: '/dashboard', icon: <HomeOutlined />, label: t('nav.dashboard') },
    { key: '/explorer', icon: <FolderOpenOutlined />, label: t('nav.explorer') },
    { key: '/people', icon: <TeamOutlined />, label: t('nav.people') },
    { key: '/issues', icon: <WarningOutlined />, label: t('nav.issues') },
    { key: '/calendar', icon: <CalendarOutlined />, label: t('nav.calendar') },
    { key: '/profile', icon: <UserOutlined />, label: t('nav.profile') },
    ...(hasPermission('deleted_data.view')
      ? [{ key: '/deleted-data', icon: <DeleteOutlined />, label: t('nav.deletedData') }]
      : []),
    { key: '/settings', icon: <SettingOutlined />, label: t('nav.settings') },
    { key: '/activity-feed', icon: <HistoryOutlined />, label: t('nav.activityFeed') },
    ...(user?.is_system_admin && !isImpersonating
      ? [{ key: '/impersonate', icon: <UserSwitchOutlined />, label: t('nav.impersonate') }]
      : []),
  ];

  const handleMenuClick = (e: { key: string }) => {
    navigate(e.key);
  };

  const handleLogout = async () => {
    try {
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch {
      // ignore
    }
    logout();
    navigate('/login');
  };

  const toggleLang = () => {
    const newLang = i18n.language === 'tr' ? 'en' : 'tr';
    i18n.changeLanguage(newLang);
    localStorage.setItem('mymaintenances-lang', newLang);
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('nav.profile'),
      onClick: () => navigate('/profile'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth.logout'),
      danger: true,
      onClick: handleLogout,
    },
  ];

  const themeMenuItems: MenuProps['items'] = palettes.map((p) => ({
    key: p.key,
    label: (
      <Space>
        <span
          style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            borderRadius: 3,
            background: p.colorPrimary,
            border: p.key === paletteKey ? '2px solid #fff' : '1px solid #555',
            verticalAlign: 'middle',
          }}
        />
        {t(p.labelKey)}
      </Space>
    ),
    onClick: () => setPalette(p.key),
  }));

  return (
    <Layout className="main-layout">
      <Header>
        <div className="header-left">
          <h2>🔧 MyMaintenances</h2>
        </div>
        <div className="header-right">
          <NotificationBell />
          <Dropdown menu={{ items: themeMenuItems, selectedKeys: [paletteKey] }} placement="bottomRight">
            <Button
              type="text"
              icon={<BgColorsOutlined />}
              style={{ color: '#999' }}
            />
          </Dropdown>
          <Button
            type="text"
            icon={<GlobalOutlined />}
            onClick={toggleLang}
            style={{ color: '#999' }}
          >
            {i18n.language === 'tr' ? 'TR' : 'EN'}
          </Button>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer', color: '#ccc' }}>
              <Avatar size="small" icon={<UserOutlined />} style={{ background: '#1677ff' }} />
              <span style={{ fontSize: 13 }}>
                {user?.first_name} {user?.last_name}
              </span>
            </Space>
          </Dropdown>
        </div>
      </Header>
      <div className="content-body">
        <div className={`tab-sidebar ${menuCollapsed ? 'tab-sidebar-collapsed' : ''}`}>
          <Menu
            mode="inline"
            inlineCollapsed={menuCollapsed}
            selectedKeys={[currentTab]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ flex: 1 }}
          />
        </div>
        <Content className="tab-content">
          {isImpersonating && (
            <Alert
              type="warning"
              showIcon
              banner
              message={t('impersonate.banner', {
                name: `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || user?.email,
              })}
              action={
                <Button
                  size="small"
                  danger
                  onClick={async () => {
                    // Log the end of the impersonation session before dropping
                    // the impersonation tokens. Best-effort: ignore failures
                    // so the user still gets back to their admin session.
                    try { await authApi.endImpersonation(); } catch { /* noop */ }
                    stopImpersonation();
                    navigate('/dashboard');
                  }}
                >
                  {t('impersonate.exit')}
                </Button>
              }
            />
          )}
          <Outlet />
        </Content>
      </div>
    </Layout>
  );
}
